import { Query, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { GeneratedAPIInstance } from 'api/generatedAPIInstance';

import type { LogAccessProfile } from './logAccess.types';
import { parseLogProfile } from './logAccess.utils';
import { useLogAccess } from './useLogAccess';

export const useLogAccessProfile = (
	sessionId: number,
	authenticated: boolean,
): UseQueryResult<LogAccessProfile> => {
	const queryClient = useQueryClient();
	const profileKey = ['log-access', 'profile', sessionId];
	return useQuery(
		profileKey,
		async ({ signal }) => {
			const response = await GeneratedAPIInstance<unknown>({
				url: '/api/v1/log_access/me',
				method: 'GET',
				signal,
			});
			if (signal?.aborted) {
				throw new Error('Access verification was cancelled.');
			}
			const nextProfile = parseLogProfile(response);
			const previousProfile =
				queryClient.getQueryData<LogAccessProfile>(profileKey);
			if (
				previousProfile &&
				JSON.stringify(previousProfile) !== JSON.stringify(nextProfile)
			) {
				const otherQueries = {
					predicate: (query: Query): boolean =>
						!Array.isArray(query.queryKey) || query.queryKey[0] !== 'log-access',
				};
				await queryClient.cancelQueries(otherQueries);
				if (signal?.aborted) {
					throw new Error('Access verification was cancelled.');
				}
				queryClient.removeQueries(otherQueries);
			}
			useLogAccess.setState(nextProfile, true);
			return nextProfile;
		},
		{
			enabled: authenticated,
			retry: false,
			refetchOnWindowFocus: true,
			refetchInterval: 60000,
			cacheTime: 0,
		},
	);
};
